import React, { useMemo } from 'react'

import { useLayerApiUrl } from './ImportDataContext'
import {
  type BusinessIndustry,
  type CustomAccount,
  getTrips,
  getVehicles,
  ImportApiError,
  validateTrips,
  validateVehicles,
} from './importDummyDataHelpers'
import { ImportForm } from './ImportForm'
import { type ImportStepDef } from './useImportForm'

type Vehicle = { external_id: string, id: string }

const isVehicle = (value: unknown): value is Vehicle => {
  if (value === null || typeof value !== 'object') {
    return false
  }

  const candidate = value as Record<string, unknown>
  return typeof candidate.external_id === 'string'
    && typeof candidate.id === 'string'
}

const isVehicleArray = (value: unknown): value is Vehicle[] => {
  return Array.isArray(value) && value.every(isVehicle)
}

const isWrappedVehicles = (value: unknown): value is { data: Vehicle[] } => {
  if (value === null || typeof value !== 'object') {
    return false
  }

  return isVehicleArray((value as { data: unknown }).data)
}

const extractVehicles = (vehicleData: unknown): Vehicle[] => {
  if (isVehicleArray(vehicleData)) {
    return vehicleData
  }

  if (isWrappedVehicles(vehicleData)) {
    return vehicleData.data
  }

  throw new Error(
    `Unexpected vehicle response shape: ${JSON.stringify(vehicleData)}`,
  )
}

export interface ImportMileageProps {
  demoName: string
  businessId: string
  industry: BusinessIndustry
  businessAccessToken: string
  defaultCustomAccount: CustomAccount
}

interface MileageData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  vehicles: any[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trips: any[]
}

const buildMileageSteps = (
  businessId: string,
  businessAccessToken: string,
  layerApiUrl: string,
) => (data: MileageData): ImportStepDef[] => {
  const batchSize = 100
  const tripBatchCount = Math.max(1, Math.ceil(data.trips.length / batchSize))
  let tripsWithVehicleIds: unknown[] = []
  return [
    {
      label: 'Creating vehicles',
      run: async () => {
        const vehicleResponse = await fetch(
          `${layerApiUrl}/v1/businesses/${businessId}/mileage/vehicles/bulk`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${businessAccessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ vehicles: data.vehicles }),
          },
        )
        if (!vehicleResponse.ok) {
          const errorText = await vehicleResponse.text()
          throw new ImportApiError(
            `Vehicle creation failed (${vehicleResponse.status}): ${errorText}`,
            vehicleResponse.status,
          )
        }
        const vehicleData = await vehicleResponse.json()
        const vehicles = extractVehicles(vehicleData)
        const vehicleIdMap = new Map<string | undefined, string>(
          vehicles.map(vehicle => [vehicle.external_id, vehicle.id]),
        )
        tripsWithVehicleIds = data.trips.map(
          (trip: { vehicle_external_id?: string; [key: string]: unknown }) => {
            const vehicleId = vehicleIdMap.get(trip.vehicle_external_id)
            const { ...tripWithoutExternalId } = trip
            return {
              ...tripWithoutExternalId,
              vehicle_id: vehicleId,
            }
          },
        )
      },
    },
    ...Array.from({ length: tripBatchCount }, (_, batchIdx) => ({
      label: `Creating trips batch ${batchIdx + 1}/${tripBatchCount}`,
      run: async () => {
        const start = batchIdx * batchSize
        const batch = tripsWithVehicleIds.slice(start, start + batchSize)
        const tripResponse = await fetch(
          `${layerApiUrl}/v1/businesses/${businessId}/mileage/trips/bulk`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${businessAccessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ trips: batch }),
          },
        )
        if (!tripResponse.ok) {
          const errorText = await tripResponse.text()
          throw new ImportApiError(
            `Trip creation failed (batch ${batchIdx + 1}, ${tripResponse.status}): ${errorText}`,
            tripResponse.status,
          )
        }
      },
    })),
  ]
}

export const ImportMileage = ({
  demoName,
  businessId,
  businessAccessToken,
  defaultCustomAccount,
}: ImportMileageProps) => {
  const layerApiUrl = useLayerApiUrl()
  const getSteps = useMemo(
    () => buildMileageSteps(businessId, businessAccessToken, layerApiUrl),
    [businessId, businessAccessToken, layerApiUrl],
  )

  return (
    <ImportForm
      title='Mileage (Vehicles & Trips)'
      defaultCustomAccount={defaultCustomAccount}
      getInitialData={() => ({
        vehicles: getVehicles(demoName),
        trips: getTrips(demoName),
      })}
      validateData={data => validateVehicles(data.vehicles) && validateTrips(data.trips)}
      getSteps={getSteps}
      dependencies={[demoName]}
    />
  )
}
